import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ProjectInquiryForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    budget: '',
    timeline: '',
    description: '',
    hearAbout: '',
    newsletter: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const projectTypes = [
    { value: 'web-app', label: 'Web Application Development' },
    { value: 'mobile-app', label: 'Mobile App Development' },
    { value: 'ecommerce', label: 'E-commerce Platform' },
    { value: 'api', label: 'API Development & Integration' },
    { value: 'consulting', label: 'Technical Consulting' },
    { value: 'maintenance', label: 'Maintenance & Support' },
    { value: 'other', label: 'Other (Specify in description)' }
  ];

  const budgetRanges = [
    { value: 'under-10k', label: 'Under $10,000' },
    { value: '10k-25k', label: '$10,000 - $25,000' },
    { value: '25k-50k', label: '$25,000 - $50,000' },
    { value: '50k-100k', label: '$50,000 - $100,000' },
    { value: 'over-100k', label: 'Over $100,000' },
    { value: 'flexible', label: 'Flexible / To be discussed' }
  ];

  const timelines = [
    { value: 'urgent', label: 'Urgent (1-2 weeks)' },
    { value: 'short', label: 'Short-term (1-2 months)' },
    { value: 'medium', label: 'Medium-term (3-6 months)' },
    { value: 'long', label: 'Long-term (6+ months)' },
    { value: 'ongoing', label: 'Ongoing Partnership' }
  ];

  const hearAboutOptions = [
    { value: 'search', label: 'Search Engine (Google, Bing)' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'referral', label: 'Referral from colleague' },
    { value: 'portfolio', label: 'Portfolio website' },
    { value: 'github', label: 'GitHub' },
    { value: 'other', label: 'Other' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.projectType) {
      newErrors.projectType = 'Please select a project type';
    }

    if (!formData?.budget) {
      newErrors.budget = 'Please select a budget range';
    }

    if (!formData?.timeline) {
      newErrors.timeline = 'Please select a timeline';
    }

    if (!formData?.description?.trim()) {
      newErrors.description = 'Project description is required';
    } else if (formData?.description?.trim()?.length < 50) {
      newErrors.description = 'Please provide at least 50 characters describing your project';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          access_key: 'f1d6166a-9c1f-4337-9491-d6bacdd3d70f',
          subject: `New Project Inquiry from ${formData.fullName}`,
          from_name: formData.fullName,
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone || 'Not provided',
          company: formData.company || 'Not provided',
          project_type: formData.projectType,
          budget_range: formData.budget,
          timeline: formData.timeline,
          message: formData.description,
          referral_source: formData.hearAbout || 'Not provided',
          newsletter_subscription: formData.newsletter ? 'Yes' : 'No'
        })
      });

      const result = await response.json();

      if (result.success) {
        setSubmitSuccess(true);
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          company: '',
          projectType: '',
          budget: '',
          timeline: '',
          description: '',
          hearAbout: '',
          newsletter: false
        });

        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      } else {
        setSubmitError(result.message || 'Something went wrong while sending the form. Please try again.');
      }
    } catch (err) {
      setSubmitError('Unable to connect to the mail server. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (submitError) {
      setSubmitError('');
    }
  };

  if (submitSuccess) {
    return (
      <div className="p-6 md:p-8 lg:p-10 rounded-lg bg-success/10 border border-success/20">
        <div className="flex flex-col items-center text-center space-y-4">
          <div className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-success/20">
            <Icon name="CheckCircle2" size={40} color="var(--color-success)" />
          </div>
          <h3 className="text-xl md:text-2xl lg:text-3xl font-bold font-heading text-foreground">
            Thank You for Reaching Out!
          </h3>
          <p className="text-sm md:text-base text-muted-foreground max-w-md">
            Your project inquiry has been received successfully. I'll review your details and get back to you within 24 hours with next steps.
          </p>
          <Button
            variant="outline"
            onClick={() => setSubmitSuccess(false)}
            iconName="RotateCcw"
            iconPosition="left"
          >
            Submit Another Inquiry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-10 rounded-lg bg-card border border-border">
      <div className="mb-6 md:mb-8">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground mb-3">
          Start Your Project
        </h2>
        <p className="text-sm md:text-base text-muted-foreground">
          Fill out the form below with your project details. The more information you provide, the better I can understand your needs and provide an accurate proposal.
        </p>
      </div>

      {submitError && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-center gap-2">
          <Icon name="AlertCircle" size={18} className="flex-shrink-0" />
          <span>{submitError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Input
            label="Full Name"
            type="text"
            placeholder="John Doe"
            value={formData?.fullName}
            onChange={(e) => handleChange('fullName', e?.target?.value)}
            error={errors?.fullName}
            required
          />

          <Input
            label="Email Address"
            type="email"
            placeholder="john@company.com"
            value={formData?.email}
            onChange={(e) => handleChange('email', e?.target?.value)}
            error={errors?.email}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Input
            label="Phone Number"
            type="tel"
            placeholder="+1 (555) 123-4567"
            value={formData?.phone}
            onChange={(e) => handleChange('phone', e?.target?.value)}
            description="Optional - for urgent project discussions"
          />

          <Input
            label="Company Name"
            type="text"
            placeholder="Your Company Inc."
            value={formData?.company}
            onChange={(e) => handleChange('company', e?.target?.value)}
            description="Optional"
          />
        </div>

        <Select
          label="Project Type"
          placeholder="Select project type"
          options={projectTypes}
          value={formData?.projectType}
          onChange={(value) => handleChange('projectType', value)}
          error={errors?.projectType}
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Select
            label="Budget Range"
            placeholder="Select budget range"
            options={budgetRanges}
            value={formData?.budget}
            onChange={(value) => handleChange('budget', value)}
            error={errors?.budget}
            required
          />

          <Select
            label="Project Timeline"
            placeholder="Select timeline"
            options={timelines}
            value={formData?.timeline}
            onChange={(value) => handleChange('timeline', value)}
            error={errors?.timeline}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Project Description <span className="text-destructive">*</span>
          </label>
          <textarea
            value={formData?.description}
            onChange={(e) => handleChange('description', e?.target?.value)}
            placeholder="Describe your project goals, key features, target audience, and any specific requirements. Include technical details if available."
            rows={6}
            className={`w-full px-4 py-3 rounded-lg bg-background border ${
              errors?.description ? 'border-destructive' : 'border-border'
            } text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 resize-none text-sm md:text-base`}
          />
          {errors?.description && (
            <p className="mt-1 text-xs md:text-sm text-destructive">{errors?.description}</p>
          )}
          <p className="mt-1 text-xs md:text-sm text-muted-foreground">
            Minimum 50 characters - Current: {formData?.description?.length}
          </p>
        </div>

        <Select
          label="How did you hear about me?"
          placeholder="Select an option"
          options={hearAboutOptions}
          value={formData?.hearAbout}
          onChange={(value) => handleChange('hearAbout', value)}
          description="Optional - helps me understand my reach"
        />

        <div className="pt-4 border-t border-border">
          <Checkbox
            label="Subscribe to newsletter"
            description="Receive occasional updates about new projects, technical insights, and industry trends. No spam, unsubscribe anytime."
            checked={formData?.newsletter}
            onChange={(e) => handleChange('newsletter', e?.target?.checked)}
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-4 pt-4 w-full">
          <div className="flex-1 min-w-0">
            <Button
              type="submit"
              variant="default"
              size="lg"
              loading={isSubmitting}
              iconName="Send"
              iconPosition="right"
              fullWidth
            >
              {isSubmitting ? 'Submitting...' : 'Submit Project Inquiry'}
            </Button>
          </div>
          <div className="flex-1 min-w-0">
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => {
                setFormData({
                  fullName: '',
                  email: '',
                  phone: '',
                  company: '',
                  projectType: '',
                  budget: '',
                  timeline: '',
                  description: '',
                  hearAbout: '',
                  newsletter: false
                });
                setErrors({});
                setSubmitError('');
              }}
              iconName="RotateCcw"
              iconPosition="left"
              fullWidth
            >
              Reset Form
            </Button>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-muted/50 border border-border">
          <div className="flex items-start gap-3">
            <Icon name="Shield" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div className="text-xs md:text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Your privacy is protected</p>
              <p>All information is encrypted and will only be used to respond to your inquiry. I never share client information with third parties.</p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProjectInquiryForm;